import React from 'react';
import { Bell, Pin, Loader2, Megaphone } from 'lucide-react';
import { useAnnouncements } from '../../hooks/useStudent';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { createSafeHtml } from '@/utils/sanitize';

interface AnnouncementsTabProps {
  moduleElementId: string;
  // Past year = read-only consultation; absent = current year
  academicYearId?: string;
  // Former group's course instance; absent = the current group
  groupeId?: string;
}

const AnnouncementsTab: React.FC<AnnouncementsTabProps> = ({ moduleElementId, academicYearId, groupeId }) => {
  const { data: announcements = [], isLoading, error } = useAnnouncements(moduleElementId, academicYearId, groupeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement des annonces
      </div>
    );
  }

  if (announcements.length === 0) {
    return (
      <div className="text-center py-12">
        <Megaphone className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Aucune annonce</h3>
        <p className="mt-2 text-sm text-gray-500">
          Votre professeur n'a pas encore publié d'annonce pour ce cours.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Bell className="h-5 w-5 text-blue-600" />
        Annonces du cours
      </h2>

      <div className="space-y-4">
        {announcements.map((announcement) => (
          <div
            key={announcement.id}
            className={`bg-white rounded-lg border ${
              announcement.isPinned ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200'
            } p-5`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {announcement.isPinned && (
                  <Pin className="h-4 w-4 text-blue-600" />
                )}
                <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
              </div>
              <span className="text-xs text-gray-500">
                {format(new Date(announcement.createdAt), "d MMM yyyy 'à' HH:mm", { locale: fr })}
              </span>
            </div>

            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={createSafeHtml(announcement.content)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnnouncementsTab;
